/*
  <vz-garage> — the saved-vehicle tab and drawer.

  Storage lives in vz-garage.js; this is presentation only.

  The behaviour that matters most is what happens when a shopper SWITCHES cars
  while standing on a filtered collection. The naive version rebuilds the URL
  from scratch, which silently throws away their category filter, their price
  range and their sort order. Instead we rewrite only the vehicle keys and leave
  every other parameter untouched — see applyVehicleToSearch.
*/

import {
  readList, readVehicle, selectVehicle, removeVehicle, clearSelection, migrate, vehicleKey,
} from './vz-garage.js';

// '?v=' is a manual cache-buster — see the note in vz-vehicle-finder.js. Keep
// the value identical in vz-vehicle-finder.js, vz-garage-ui.js and vz-recs.js.
import {
  applyVehicleToSearch, clearVehicleFromSearch, searchUrl, vehicleName,
} from './vz-fitment-core.js?v=20261004a';

// The store-wide lock (see snippets/vz-vehicle-lock.liquid). Armed with the
// finished filter query whenever a car becomes the selected one; removed by
// every control that un-selects it.
const GARAGE_QUERY_KEY = 'vz_garage_query';

function armLock(vehicle) {
  try {
    // A garage pick is a make/model/year/engine car, not a VIN: drop any old VIN check.
    window.sessionStorage.removeItem('vz_vin_decode');
    window.localStorage.setItem(GARAGE_QUERY_KEY, applyVehicleToSearch('', vehicle));
  } catch (error) { /* no storage */ }
}

function disarmLock() {
  try {
    window.localStorage.removeItem(GARAGE_QUERY_KEY);
    window.sessionStorage.removeItem('vz_vin_decode');
  } catch (error) { /* no storage */ }
}

class VzGarage extends HTMLElement {
  connectedCallback() {
    this.strings = this.#json('strings') || {};
    this.config = this.#json('config') || {};

    this.tab = this.querySelector('[data-garage-tab]');
    this.panel = this.querySelector('[data-garage-panel]');
    this.overlay = this.querySelector('[data-garage-overlay]');
    this.list = this.querySelector('[data-garage-list]');
    this.label = this.querySelector('[data-garage-label]');
    this.closeButton = this.querySelector('[data-garage-close]');

    // Pull a pre-list-era saved vehicle into the list so it does not look lost.
    migrate();

    this.#bind();
    this.render();
  }

  #json(name) {
    const node = this.querySelector(`[data-${name}]`);
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      return null;
    }
  }

  /* "Range Rover Sport 2016". Public: the results-page toast in
     vz-collection.js calls it so the toast and the drawer always agree. */
  describe(vehicle) {
    if (!vehicle) return '';
    const name = vehicleName(vehicle.make, vehicle.model, document.documentElement.lang);
    return [name, vehicle.year].filter(Boolean).join(' ');
  }

  #bind() {
    this.tab.addEventListener('click', () => this.toggle());
    this.closeButton.addEventListener('click', () => this.close());
    this.overlay.addEventListener('click', () => this.close());

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.hasAttribute('open')) this.close();
    });

    this.list.addEventListener('click', (event) => {
      const action = event.target.closest('[data-action]');
      if (!action) return;

      const row = action.closest('[data-key]');
      if (!row) return;

      const vehicle = readList().find((entry) => vehicleKey(entry) === row.dataset.key);
      if (!vehicle) return;

      if (action.dataset.action === 'select') this.#switch(vehicle);
      if (action.dataset.action === 'remove') {
        const selected = readVehicle();
        const wasSelected = selected && vehicleKey(selected) === vehicleKey(vehicle);
        removeVehicle(vehicle);
        if (wasSelected) disarmLock();
        this.render();
      }
    });

    // Anything anywhere on the page can open the garage by carrying this
    // attribute — currently the header button, which lives outside this element.
    document.addEventListener('click', (event) => {
      if (event.target.closest('[data-vz-garage-open]')) this.open();
    });

    const clear = this.querySelector('[data-garage-clear]');
    if (clear) {
      clear.addEventListener('click', () => {
        clearSelection();
        disarmLock();
        // Drop the vehicle filters but keep everything else the shopper chose.
        if (this.#onFilterablePage()) {
          window.location.search = clearVehicleFromSearch(window.location.search);
          return;
        }
        this.render();
      });
    }
  }

  /* Rewriting the query only makes sense somewhere that renders a product list.
     Anywhere else, switching cars should navigate to the collection instead. */
  #onFilterablePage() {
    return /\/collections\//.test(window.location.pathname) || /\/search/.test(window.location.pathname);
  }

  #switch(vehicle) {
    selectVehicle(vehicle);
    armLock(vehicle);

    if (this.#onFilterablePage()) {
      const next = applyVehicleToSearch(window.location.search, vehicle);
      window.location.search = next;
      return;
    }

    const url = searchUrl(vehicle, this.config.collection, this.config.root);
    if (url) window.location.href = url;
    else this.render();
  }

  /* ------------------------------------------------------------- open/close -- */

  toggle() {
    if (this.hasAttribute('open')) this.close();
    else this.open();
  }

  open() {
    this.setAttribute('open', '');
    this.tab.setAttribute('aria-expanded', 'true');
    this.panel.removeAttribute('inert');

    // Remember where focus came from so Escape can hand it back.
    this.returnFocus = document.activeElement;
    const first = this.panel.querySelector('button, [href]');
    if (first) first.focus();
  }

  close() {
    this.removeAttribute('open');
    this.tab.setAttribute('aria-expanded', 'false');
    this.panel.setAttribute('inert', '');

    if (this.returnFocus && document.contains(this.returnFocus)) this.returnFocus.focus();
  }

  /* ----------------------------------------------------------------- render -- */

  /* Mirrors the garage state onto triggers living elsewhere in the page. */
  #syncTriggers(selected) {
    const label = selected ? this.describe(selected) : this.strings.noVehicle || 'Your garage';

    document.querySelectorAll('[data-vz-garage-open]').forEach((trigger) => {
      trigger.hidden = false;
      const slot = trigger.querySelector('[data-vz-garage-label]');
      if (slot) slot.textContent = label;
    });
  }

  render() {
    const list = readList();
    const selected = readVehicle();

    // With nothing saved the tab is noise, so it is removed from the layout and
    // from the accessibility tree entirely rather than just hidden.
    this.hidden = list.length === 0;
    if (!list.length) {
      document.querySelectorAll('[data-vz-garage-open]').forEach((trigger) => {
        trigger.hidden = true;
      });
      this.close();
      return;
    }

    this.#syncTriggers(selected);

    this.label.textContent = selected
      ? this.describe(selected)
      : this.strings.noVehicle || 'Your garage';

    this.list.innerHTML = '';

    for (const vehicle of list) {
      const isSelected = selected && vehicleKey(selected) === vehicleKey(vehicle);

      const row = document.createElement('li');
      row.className = 'vz-garage__item';
      row.dataset.key = vehicleKey(vehicle);
      if (isSelected) row.classList.add('is-selected');

      const select = document.createElement('button');
      select.type = 'button';
      select.className = 'vz-garage__select';
      select.dataset.action = 'select';
      select.setAttribute('aria-current', isSelected ? 'true' : 'false');

      const name = document.createElement('span');
      name.className = 'vz-garage__name';
      name.textContent = this.describe(vehicle);
      select.appendChild(name);

      if (vehicle.engine) {
        const engine = document.createElement('span');
        engine.className = 'vz-garage__engine';
        engine.textContent = vehicle.engine;
        select.appendChild(engine);
      }

      if (isSelected) {
        const badge = document.createElement('span');
        badge.className = 'vz-badge vz-badge--quiet vz-garage__badge';
        badge.textContent = this.strings.active || 'Active';
        select.appendChild(badge);
      }

      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'vz-garage__remove';
      remove.dataset.action = 'remove';
      // The row already announces the vehicle, so the icon button needs a label
      // that says WHICH car it removes — "Remove" alone is useless in a list.
      remove.setAttribute('aria-label',
        `${this.strings.remove || 'Remove'} ${this.describe(vehicle)}`);
      remove.innerHTML =
        '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" ' +
        'stroke-width="2" stroke-linecap="round" aria-hidden="true" focusable="false">' +
        '<path d="M1 1l12 12M13 1L1 13"/></svg>';

      row.append(select, remove);
      this.list.appendChild(row);
    }
  }
}

if (!customElements.get('vz-garage')) {
  customElements.define('vz-garage', VzGarage);
}